<script>
    import { preventDefault } from 'svelte/legacy';
    import { createEventDispatcher, onDestroy } from 'svelte';
    import { params, url, goto } from '@roxi/routify';
    import userService from './service.svelte.js';
    import { notificate } from '../../services/notification.js';
    import { translateErrorMessages, flatten422Errors } from '../../services/api.svelte.js';
    import { form as svForm, field } from "svelte-forms";
    import { required } from 'svelte-forms/validators';
    import { removeTags } from '../../js/utils.js';
    import { format, locale } from 'svelte-i18n';
    import MassiveActionsService from '../../services/massive-actions.js';

    const dispatch = createEventDispatcher();


    let inputElement;
    let id = $params.id;
    let formDataPromise = $state();
    let formData = $state({});
    let formIsSubmitted = false;
    let loading = $state(false);
    // API string errors
    let APIerror = $state(null);

    // API 422 errors
    let formApiErrors = $state({});

    formData.users = '';

    // add form
    if (!id) {
        formDataPromise = Promise.resolve(formData={});
    }

    const usersField = field('users', '', [required()]);
    const myForm = svForm(usersField);

    // Hard wipe cache and inputs when component is destroyed (closed via key refresh)
    onDestroy(() => {
        formData.users = '';
        if ($usersField) $usersField.value = '';
        formApiErrors = {};
        APIerror = null;
        formIsSubmitted = false;
    });

    // errors must be shown only when:
    //   field is invalid (dirty or after submit)
    //   or the API returned 422
    function shouldShowFieldError(field) {
        return (
            ((formIsSubmitted || field.dirty) && !field.valid)
            || (formApiErrors[field])
        );
    }

    async function handleSubmit() {
        loading = true;
        let config = {
            add: true
        }

        formIsSubmitted = true;
        // reset API errors
        formApiErrors = {};

        if (!$myForm.valid) {
            notificate('422');
            loading = false;
            return false;
        }

        if(formData.users) {
            let data = [];
            let users = formData.users.split("\n");
            if(users.length > 50){
                loading = false;
                return APIerror = 'Por favor, cargue hasta 50 registros como máximo';
            }

            for(let [idx, user] of Object.entries(users)){
                user = {email: user, role: formData.role, language: formData.language};
                for(let [k, v] of Object.entries(user)){
                    if(typeof v == 'string'){
                        formData[k] = removeTags(v);
                    }
                }
                data.push(user);
            }
            let response = await MassiveActionsService.handleMassiveAddition(data, {action: "add", elements_type: "user"});
            let results  = response.results;
            let errors   = response.errors;
            
            // Wipe inputs on success
            formData.users = '';
            if ($usersField) $usersField.value = '';
            
            dispatch("save", {results: results, errors: errors});
        }
        formIsSubmitted = false;
        formApiErrors = {};
        APIerror = null;
        loading = false;
        return true;
    }

    function handleCloseModal(){
        formIsSubmitted = false;
        formData.users = '';
        if ($usersField) $usersField.value = '';
        dispatch('cancel'); // Tell parent to hide the modal and wipe inputs
    }

    const handleKeydown = event => {
        if(event.key == "Escape"){
            handleCloseModal();
        }
        else {
            return;
        }
    }

    let is_loading = $derived(loading);
    let languages = $derived(userService.getLangOptions($locale));
</script>

<style>
    textarea {
        min-height: 100px;
    }
    .alert.alert-warning {
        color: #856404;
        background-color: #fff3cd;
    }
</style>

<svelte:window onkeydown={handleKeydown}/>

<form role="form" novalidate onsubmit={preventDefault(handleSubmit)}>
    <div class="card">
        <div class="card-body">
            {#await formDataPromise then formDataUnused}
            <div class="mb-3">
                <label for="users">{$format("users")}<span title="Requerido" class="text-danger">*</span></label>
                <textarea
                    type="text"
                    class="form-control"
                    id="users"
                    name="users"
                    placeholder="{$format("form.fields.users_placeholder")}"
                    bind:value={formData.users}
                    disabled={formData.id}
                    class:is-invalid={shouldShowFieldError($usersField)}
                ></textarea>
                {#if APIerror}
                    <div class="mt-3 text-danger" style="font-size: 80%;">{APIerror}</div>
                {/if}
                {#if shouldShowFieldError($usersField)}
                    {#if $usersField.includes('required')}
                        <div class="invalid-feedback">
                            {$format("required")}
                        </div>
                    {/if}
                {/if}
            </div>
            {/await}
            <div class="mb-3">
                <label for="role">{$format("list.columns.role")}</label>
                <select
                    id="role"
                    name="role"
                    class="t-input-user-role form-control"
                    bind:value={formData.role}
                >
                {#each Object.entries(userService.roles) as [value, label]}
                    <option value={value}>
                        {$format(`page.user.roles.${value}`)}
                    </option>
                {/each}
                </select>
            </div>
            <div class="mb-3">
                <label for="language">{$format("form.fields.language")}</label>
                <select id="language" class="form-control" bind:value={formData.language}>
                    {#each languages as { code, name }}
                        <option value="{code}">{name}</option>
                    {/each}
                </select>
            </div>
            <small class="alert alert-warning d-block">{$format("page.user.role_warning")}</small>
            <div>
                {#key formApiErrors}
                    {#each Object.entries(formApiErrors) as [k, v]}
                        <span class="d-block mt-3 text-danger">{k}: {v}</span>
                    {/each}
                {/key}
            </div>
            {#if !is_loading}
                <div class="card-footer">
                    <button type="submit" class="btn btn-primary t-submit-user-btn">{$format("buttons.save")}</button>
                    <!-- Added type="button" and removed data-bs-dismiss -->
                    <button type="button" class="btn btn-default" onclick={handleCloseModal}>{$format("buttons.cancel")}</button>
                </div>
            {:else}
                <div class="overlay">
                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                </div>
            {/if}
            {#await formDataPromise}
                <div class="overlay">
                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                </div>
            {/await}
        </div>
    </div>
</form>